import type { JSX, ReactNode } from 'react';
import { Fragment, createContext, useContext, useEffect, useRef } from 'react';
import type { DefaultTheme } from 'styled-components';
import { ThemeProvider } from 'styled-components';

import * as actions from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import * as models from 'pkg/api/models';
import { normalizeMembership } from 'pkg/api/models/membership/utils';
import { useEndpoint } from 'pkg/api/use_endpoint';
import { resume } from 'pkg/events';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import { useAccountIdentity } from 'pkg/identity/account';
import * as logger from 'pkg/logger';
import { Router, IndexRoute, Route } from 'pkg/router';
import { replaceState } from 'pkg/router/state';

import { Spinner } from 'components/loaders/spinner';
import OrganizationAuthGuard from 'components/organization/auth_guard';

import { setApiCurrentUser } from 'core/api_auth';

function getCurrentUrl() {
	let currentUrl = window.location.pathname;

	if (window.location.search.length > 0) {
		currentUrl += window.location.search;
	}

	return currentUrl;
}

interface OrganizationIdentityProviderProps {
	children: ReactNode;
}

interface OrganizationIdentityState {
	organization: models.group.Group;
	user: models.user.User;

	wards: models.user.User[];

	refreshOrganizationIdentity: () => Promise<void>;
}

const DefaultOrganizationIdentityState: OrganizationIdentityState = {
	organization: null,
	user: null,
	wards: [],
	refreshOrganizationIdentity: () => null,
};

export const OrganizationIdentityContext =
	createContext<OrganizationIdentityState>(DefaultOrganizationIdentityState);

let __organizationIdentityContextValues: Readonly<
	Partial<OrganizationIdentityState>
>;

/**
 *	@warn This is is an "unsafe" way of getting current context values, and is *only* used in router middlewares.
 */
export function getOrganizationIdentityContextValues(): Readonly<
	Partial<OrganizationIdentityState>
> {
	return __organizationIdentityContextValues;
}

export const useOrganizationIdentity = () => {
	return useContext(OrganizationIdentityContext);
};

function AuthGuardRoutes({
	organization,
}: {
	organization: models.group.Group;
}) {
	const redirectToHome = () => {
		replaceState('/');
	};

	return (
		<ThemeProvider theme={{} as DefaultTheme}>
			<Router
				key="auth-guard"
				currentPath={getCurrentUrl()}
				onNotFound={redirectToHome}>
				<IndexRoute
					component={OrganizationAuthGuard}
					name="auth-guard"
					organization={organization}
				/>
				<Route
					path="sso/redirect"
					name="sso-redirect"
					component={OrganizationAuthGuard}
					organization={organization}
				/>
			</Router>
		</ThemeProvider>
	);
}

interface selfResponse {
	user: models.user.User;
	children: models.user.User[];
}

export function OrganizationIdentityProvider({
	children,
}: OrganizationIdentityProviderProps): JSX.Element {
	const { organization, setOrganization } = useAccountIdentity();

	const selfEndpoint = organization?.id
		? endpoints.Selfs.ShowOrganizationSelf(organization.id)
		: undefined;

	const { record, isLoading, refresh, response } = useEndpoint<selfResponse>(
		selfEndpoint,
		{ actions: [models.Action.GroupViewHierarchy] },
		(d, response) => {
			if (!response.ok) {
				return;
			}

			setApiCurrentUser(d.user.id.toString());

			logger.setOrganizationContext(organization);
		}
	);

	// Track the endpoint URL we last successfully loaded data for. When the
	// WebView is backgrounded (e.g. Android opens a native file picker) and
	// then resumed, `resume.on(refresh)` re-fetches the same endpoint. Without
	// this, we'd unconditionally render <Spinner /> during that refresh,
	// unmounting the entire app subtree and orphaning any in-flight state
	// (such as a file upload's React state). When the current endpoint matches
	// the last-loaded one, treat isLoading as a background refresh and keep
	// rendering children. On a real org switch the endpoint URL differs, so
	// we still show the spinner.
	const lastLoadedEndpoint = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!isLoading && record?.user && selfEndpoint) {
			lastLoadedEndpoint.current = selfEndpoint;
		}
	}, [isLoading, record, selfEndpoint]);

	const isSilentRefresh =
		isLoading && lastLoadedEndpoint.current === selfEndpoint;

	useEndpoint(
		organization?.id
			? endpoints.Organizations.Show(organization.id)
			: undefined,
		{},
		(group: models.group.Group, response) => {
			if (!response.ok) {
				return;
			}
			actions.organizations.hydrateSplashScreenStateFromGroup(group);
			setOrganization(group);
		}
	);

	useComponentDidMount(
		() => {
			resume.on(refresh);
		},
		() => {
			resume.off(refresh);
		}
	);

	if (!organization?.id) {
		return <Fragment>{children}</Fragment>;
	}

	const user = record?.user
		? {
				...record.user,
				groups: record.user.groups?.map(normalizeMembership),
			}
		: null;

	const wards =
		record?.children?.map((child) => ({
			...child,
			groups: child.groups?.map(normalizeMembership),
		})) ?? [];

	const providedState = {
		user,
		wards,
		organization,
		refreshOrganizationIdentity: refresh,
	};

	__organizationIdentityContextValues = Object.freeze(providedState);

	if (response?.status === 401) {
		children = <AuthGuardRoutes organization={organization} />;
	}

	if (isLoading && !isSilentRefresh) {
		return <Spinner center />;
	}

	return (
		<OrganizationIdentityContext.Provider value={providedState}>
			{children}
		</OrganizationIdentityContext.Provider>
	);
}
